import { memo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import { reportClientError } from 'services/errorReportingService'
import { useAppSelector } from 'hooks/reduxHooks'

const Root = styled.div`
  position: relative;
  overflow: hidden;
  text-align: center;
`

const IconHeading = styled(LineExclamationCircleIcon)`
  color: ${props => props.theme.palette.neutral.default.one};
  height: ${rem(39)};
  width: ${rem(39)};
  margin: 0 auto ${rem(22)};
`

const TextHeading = styled(Heading)`
  height: ${rem(45)};
  max-width: ${rem(700)};
  margin: 0 auto ${rem(22)};
`

const TextDescription = styled(Heading)`
  height: ${rem(66)};
  max-width: ${rem(700)};
  margin: 0 auto ${rem(22)};
`

const Wrapper = styled.div`
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${rem(328)};
`

const ImageWrapper = styled.div`
  width: 100%;
  max-height: calc(100vh - ${rem(386)});
  display: flex;
  align-items: center;
  overflow: hidden;
`

const ImageContent = styled.div`
  position: absolute;
  z-index: 1;
  top: ${rem(486)};
  bottom: 0;
  left: 0;
  right: 0;
`

interface Props {
  error: any
}

function Error500Page({ error }: Props) {
  const intl = useIntl()

  useEffect(() => {
    if (!error) return
    reportClientError(error)
  }, [error])

  const state = useAppSelector(state => state)

  const pageTitle = intl.formatMessage({
    id: 'HIqlZzz0MpQzuBcgOKV2',
    defaultMessage: 'Page Not Available',
    description: 'Page title for the 500 error page shown in browser tab',
  })

  const pageDescription = intl.formatMessage({
    id: 'zdvnKpn6BrNn6ljZXAmH',
    defaultMessage: '500 - fatal error',
    description: 'HTML meta description for the 500 error page in the error recovery section',
  })

  const errorHeading = intl.formatMessage({
    id: '3OeoeMTQGwkINkksdZGc',
    defaultMessage: 'Oh no, an error has occurred',
    description: 'Main heading on the 500 error page in the error recovery section',
  })

  const errorDescription = intl.formatMessage({
    id: 'jNNK3bR1W7L5GccwtNNU',
    defaultMessage: 'There seems to be a problem on our end. Rest assured our team is working hard to repair it and all should be fixed soon.',
    description: 'Description message on the 500 error page explaining the issue',
  })

  const magazineDescription = intl.formatMessage({
    id: 'Lrd_z7uYbDYrDgmsx2Sw',
    defaultMessage: 'In the meantime check out our magazine for the latest travel videos & guides',
    description: 'Description text encouraging users to visit the magazine while the error is being fixed',
  })

  const viewMagazineLabel = intl.formatMessage({
    id: 'fw5yXOftiYOkDgN1hO8D',
    defaultMessage: 'View Magazine',
    description: 'Button label to navigate to the Luxury Escapes magazine',
  })

  const imageAlt = intl.formatMessage({
    id: '3SDGp9yLaLXR30OH6CFG',
    defaultMessage: 'Page Not Available',
    description: 'Alt text for background image on the 500 error page',
  })

  return (
    <Root className="Error500Page T-Error500Page">
      <HeadData
        title={pageTitle}
        description={pageDescription}
        noIndex
      />
      <Wrapper>
        <IconHeading />
        <TextHeading variant="heading2" align="center" forwardedAs="h1">
          {errorHeading}
        </TextHeading>
        <TextDescription variant="heading3" align="center" forwardedAs="h2">
          {errorDescription}
        </TextDescription>
        <ImageContent>
          <LayoutContainer>
            <TextHeading variant="heading2" align="center" colour="neutral-four">
              {intl.formatMessage({
                id: 'EU8PXfs8hP0qR139Ly7l',
                defaultMessage: 'Luxury Escapes Magazine',
                description: 'Heading for the Luxury Escapes Magazine section on the 500 error page',
              })}
            </TextHeading>
            <TextDescription variant="heading3" align="center" colour="neutral-four">
              {magazineDescription}
            </TextDescription>
            <TextButton
              kind="primary"
              href="http://www.luxuryescapes.com/magazine"
              target="_blank"
            >
              {viewMagazineLabel}
            </TextButton>
          </LayoutContainer>
        </ImageContent>
      </Wrapper>
      <div id="error-500-page-error" style={{ display: 'none' }} aria-hidden="true">
        {error && JSON.stringify(error)}
      </div>
      <div id="error-500-page-state" style={{ display: 'none' }} aria-hidden="true">
        {state && JSON.stringify(state, null, 2)}
      </div>
      <ImageWrapper>
        <ResponsiveImage
          aria-hidden="true"
          alt={imageAlt}
          brightness={-35}
          mobileAspectRatio="8:9"
          tabletAspectRatio="4:3"
          desktopAspectRatio="4:3"
          largeDesktopAspectRatio="16:9"
          id="nqoj6awjm2cqbghnnaor"
        />
      </ImageWrapper>
    </Root>
  )
}

export default memo(Error500Page)
